import { getConversionFactorValue } from '../conversion-factors.helpers';
import { highest } from '../math.helpers';
import type { IProposal } from '@shared/models/proposals.model';
import type {
  ConversionFactorGroupKey,
  Results,
  ResultsRecord,
} from '@shared/models/unit.model';
import { guards } from '../typeguards.helpers';

const SCALE_MIN = 1e-12;

type ProposalOverviewPerGfaMetric = Extract<
  ConversionFactorGroupKey,
  'co2e' | 'sek'
>;

export type ProposalOverviewCo2PerGfaScaleOptions = {
  /**
   * Carbon targets drawn as lines (kg CO₂e/m² GFA) — the project's own and the
   * active scope's. Finite values are included in the scale max so no line falls
   * outside the chart. Omit the option to use only baseline + proposal totals.
   */
  targetsKgCo2ePerM2?: readonly (number | null | undefined)[];
};

/**
 * Collects per-GFA-normalized values for baseline and each proposal using the
 * same record slices as the overview bars.
 */
const collectScenarioMetricPerGfa = (
  versionId: string,
  records: Record<string, ResultsRecord>,
  proposals: readonly IProposal[],
  getResultsPerGFA: (results: Results) => Results,
  factorGroup: ProposalOverviewPerGfaMetric,
): number[] => {
  const values: number[] = [];

  const baselineRollup = records[versionId]?.[versionId];
  values.push(
    getConversionFactorValue(
      getResultsPerGFA(baselineRollup ?? {}),
      factorGroup,
    ),
  );

  for (const proposal of proposals) {
    const rollup = records[proposal.id]?.[versionId];
    values.push(
      getConversionFactorValue(getResultsPerGFA(rollup ?? {}), factorGroup),
    );
  }

  return values;
};

/**
 * Vertical CO₂ scale for the proposals UI: **maximum** kg CO₂e/m² among baseline rollups
 * and each proposal rollup (same keys as the bars). Optionally fold in
 * {@link ProposalOverviewCo2PerGfaScaleOptions.targetsKgCo2ePerM2} when the overview
 * should keep the dashed target lines inside the chart.
 *
 * @param versionId - Selected building version id (rollup key).
 * @param records - Lifecycle-filtered result map (baseline row stored under `versionId`).
 */
export const computeProposalOverviewCo2PerGfaScaleMax = (
  versionId: string | undefined,
  records: Record<string, ResultsRecord>,
  proposals: readonly IProposal[],
  getResultsPerGFA: (results: Results) => Results,
  { targetsKgCo2ePerM2 = [] }: ProposalOverviewCo2PerGfaScaleOptions = {},
): number => {
  if (!versionId) return SCALE_MIN;

  const co2PerScenario = collectScenarioMetricPerGfa(
    versionId,
    records,
    proposals,
    getResultsPerGFA,
    'co2e',
  );

  const targets = targetsKgCo2ePerM2.filter(guards.is.number);

  return Math.max(highest([...co2PerScenario, ...targets]), SCALE_MIN);
};

/**
 * Vertical cost scale for the proposals overview: **maximum** SEK/m² among baseline
 * and each proposal rollup (same keys as the bars).
 */
export const computeProposalOverviewCostPerGfaScaleMax = (
  versionId: string | undefined,
  records: Record<string, ResultsRecord>,
  proposals: readonly IProposal[],
  getResultsPerGFA: (results: Results) => Results,
): number => {
  if (!versionId) return SCALE_MIN;

  const costPerScenario = collectScenarioMetricPerGfa(
    versionId,
    records,
    proposals,
    getResultsPerGFA,
    'sek',
  );

  return Math.max(highest(costPerScenario), SCALE_MIN);
};
